import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import { useQuery, useMutation } from "@tanstack/react-query";

export interface NoticeDataType {
  type: string;
  message: string;
}

export interface NewNoticeData {
  notiRecvSeq: number | null;
  reservationSeq: number | null;
  type: string | null;
  message: string;
  readYn: "Y" | "N";
  cdReservStatus: string | null;
  cdReservType: string | null;
  companyName: string | null;
  hasInvite: boolean;
}

export interface UserRegionSaveType {
  regionName: string;
  regionAddr: string;
  latitude: number;
  longitude: number;
}

export interface CompanyListParams {
  region: string;
  category?: string | null;
}

export interface CompanyMapListParams {
  swLat: number;
  swLng: number;
  neLat: number;
  neLng: number;
  category?: string | null;
}

export interface CompanySearchKeywordParams {
  keyword: string;
  category?: string | null;
  sort?: "DISTANCE" | null;
  latitude?: number | null;
  longitude?: number | null;
  page?: number;
  size?: number;
}

export interface CompanyType {
  companySeq: number;
  companyName: string;
  companyAddr: string;
  fileUrl?: string;
  masterYn?: string; // "Y"면 마스터 데이터, "N"이면 일반 회사 데이터
  activeYn?: string; // 가맹점 활성 여부. 마스터 데이터(미가입)는 항상 "N"
  latitude?: number | null;
  longitude?: number | null;
  maxWeight?: number; // 최대 수용 무게(kg)
  provides?: Array<{
    name: string;
    available: boolean;
  }>;
  weightClasses?: Array<{
    name: string;
    available: boolean;
  }>;
}

export interface TicketType {
  name: string;
  contents: string;
  cost: number;
  discount: number;
  amount: number;
  badge: string[];
  dataOrder: number | null;
}

export interface CompanyTicketType {
  productTicketSeq: number;
  companySeq: number;
  cdProductType: string;
  name: string;
  contents: string;
  cost: number;
  discount: number;
  amount: number;
  splitYn: string;
  splitCount: number;
  badge: string[];
  // 데이케어 시간권: 최소 구매/이용 수량 (null이면 제한 없음)
  minPurchaseQty?: number | null;
  minUseQty?: number | null;
}

export interface BusinessTimeType {
  cdDayOfWeek: string; // "MONDAY", "TUESDAY", etc.
  startTime: string; // "09:00:00"
  endTime: string; // "18:00:00"
  close: boolean; // 휴무 여부
}

export interface CompanyDetailType {
  companySeq: number;
  thumbnailUrls: string[] | null;
  fileUrls?: string[] | null;
  media?: {
    url: string;
    mediaType?: string | null;
    thumbnailUrl?: string | null;
    representativeYn?: string | null;
  }[] | null;
  companyName: string;
  companyAddr: string;
  masterYn?: string;
  mainTelNumber?: string;
  maxWeight?: number; // 최대 수용 무게(kg)
  playgroundYn: string;
  close: boolean;
  endTime: string | null;
  provides: Array<{
    cdProvideType: string;
    productName: string;
    active: boolean;
    select: boolean;
  }>;
  facilities: Array<{
    cdProvideType: string;
    productName: string;
    active: boolean;
    select: boolean;
  }>;
  playground: Array<{
    cdProvideType: string;
    productName: string;
    active: boolean;
    select: boolean;
  }>;
  weightClasses: Array<{
    cdProvideType: string;
    productName: string;
    active: boolean;
    select: boolean;
  }>;
  tickets?: Array<{
    purchaseTicketSeq: number;
    productTicketSeq: number;
    userSeq: number;
    name: string;
    contents: string;
    cost: number;
    discount: number;
    amount: number;
    qty: number;
    badge: string[];
    companyName: string;
    address: string;
    companySeq: number;
    purchaseDate: string;
    expiredDate: string;
    cdProductType: string;
  }>;
  businessTimes?: BusinessTimeType[];
}

export const NOTICE_QUERY_KEYS = {
  home: ["home", "assign-area", "list"],
  company: ["company", "list"],
  companyMap: ["company", "map-list"],
  companySearch: ["company", "search"],
  companyDetail: ["company", "detail"],
  companyTicketList: ["company", "ticket-list"],
  newNotice: ["home", "new-notice"],
  banner: ["home", "banner", "list"],
} as const;

export interface BannerItem {
  bannerSeq: number;
  title?: string | null;
  fileUrl?: string | null; // 텍스트-only AI 배너는 이미지가 없을 수 있음(색배경+문구)
  linkType: "NONE" | "INTERNAL" | "EXTERNAL";
  linkUrl?: string | null;
  // AI 오버레이(표시-시점 합성): overlay=true면 이미지 위에 문구를 렌더
  overlay?: boolean;
  headline?: string | null;
  subline?: string | null;
  textZone?: string | null;
  pointColor?: string | null;
  fontKey?: string | null;
}

const getBannerList = async (
  latitude?: number | null,
  longitude?: number | null
): Promise<BannerItem[]> => {
  try {
    // 위경도 전달 시 위치기반 배너는 연결매장 반경 내일 때만 포함(미전달 시 전역 배너만)
    const params =
      latitude != null && longitude != null ? { latitude, longitude } : undefined;
    const { data } = await privateHttp.get(API_ENDPOINTS.MAIN.BANNER_LIST, { params });
    return data.data ?? [];
  } catch (error) {
    console.error("배너 목록 API 호출 에러:", error);
    throw error;
  }
};

export const useBannerList = (
  latitude?: number | null,
  longitude?: number | null
) => {
  return useQuery({
    queryKey: [...NOTICE_QUERY_KEYS.banner, latitude ?? null, longitude ?? null],
    queryFn: () => getBannerList(latitude, longitude),
    staleTime: 5 * 60 * 1000, // 배너는 자주 안 바뀌므로 5분 캐시
  });
};

/** 배너 노출 기록(효과 리포트용) — 실패해도 무시(사용자 흐름 방해 X) */
export const recordBannerImpressions = async (bannerSeqs: number[]) => {
  if (!bannerSeqs.length) return;
  try {
    await privateHttp.post(API_ENDPOINTS.MAIN.BANNER_IMPRESSIONS, { bannerSeqs });
  } catch (error) {
    console.debug("배너 노출 기록 실패(무시):", error);
  }
};

/** 배너 클릭 기록 */
export const recordBannerClick = async (bannerSeq: number) => {
  try {
    await privateHttp.post(API_ENDPOINTS.MAIN.BANNER_CLICK, null, {
      params: { bannerSeq },
    });
  } catch (error) {
    console.debug("배너 클릭 기록 실패(무시):", error);
  }
};

const getAssignAreaList = async () => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.MAIN.USER_ASSIGN_AREA_LIST
    );
    // console.log("data", data);
    return data.data;
  } catch (error) {
    console.error("API 호출 에러:", error);
    throw error;
  }
};

const saveUserRegion = async (regionData: UserRegionSaveType) => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.MAIN.USER_REGION_SAVE,
      regionData
    );
    return data;
  } catch (error) {
    console.error("지역 저장 API 호출 에러:", error);
    throw error;
  }
};

const getCompanyList = async (params: CompanyListParams) => {
  try {
    const { data } = await privateHttp.get(API_ENDPOINTS.MAIN.COMPANY_LIST, {
      params: {
        region: params.region,
        ...(params.category && { category: params.category }),
      },
    });
    return data.data;
  } catch (error) {
    console.error("업체 목록 API 호출 에러:", error);
    throw error;
  }
};

const getCompanyMapList = async (params: CompanyMapListParams) => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.MAIN.COMPANY_MAP_LIST,
      {
        params: {
          swLat: params.swLat,
          swLng: params.swLng,
          neLat: params.neLat,
          neLng: params.neLng,
          ...(params.category && { category: params.category }),
        },
      }
    );
    return data.data;
  } catch (error) {
    console.error("지도 영역 업체 목록 API 호출 에러:", error);
    throw error;
  }
};

const getCompanySearch = async (params: CompanySearchKeywordParams) => {
  try {
    const { data } = await privateHttp.get(API_ENDPOINTS.MAIN.COMPANY_SEARCH, {
      params: {
        keyword: params.keyword,
        ...(params.category && { category: params.category }),
        ...(params.sort && { sort: params.sort }),
        ...(params.latitude != null && { latitude: params.latitude }),
        ...(params.longitude != null && { longitude: params.longitude }),
        page: params.page ?? 0,
        size: params.size ?? 20,
      },
    });
    return data.data;
  } catch (error) {
    console.error("업체 검색 API 호출 에러:", error);
    throw error;
  }
};

const getCompanyDetail = async (companySeq: number, masterYn?: string) => {
  try {
    const { data } = await privateHttp.get(API_ENDPOINTS.MAIN.COMPANY_DETAIL, {
      params: {
        companySeq,
        ...(masterYn && { masterYn }), // masterYn이 있으면 파라미터에 추가
      },
    });
    return data.data;
  } catch (error) {
    console.error("업체 상세 정보 API 호출 에러:", error);
    throw error;
  }
};

const getCompanyTicketList = async (companySeq: number) => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.MAIN.COMPANY_TICKET_LIST,
      {
        params: {
          companySeq,
        },
      }
    );
    return data.data;
  } catch (error) {
    console.error("업체 이용권 목록 API 호출 에러:", error);
    throw error;
  }
};

const getNewNotice = async () => {
  try {
    const { data } = await privateHttp.get(API_ENDPOINTS.MAIN.NEW_NOTICE);
    return data.data;
  } catch (error) {
    console.error("새 공지사항 API 호출 에러:", error);
    throw error;
  }
};

export const deleteUserRegion = async (regionObj: any) => {
  try {
    const { data } = await privateHttp.delete(
      API_ENDPOINTS.MAIN.USER_REGION_DELETE,
      { data: regionObj }
    );
    return data;
  } catch (error) {
    console.error("지역 삭제 API 호출 에러:", error);
    throw error;
  }
};

export const useAssignAreaList = () => {
  return useQuery({
    queryKey: [...NOTICE_QUERY_KEYS.home, "assign-area"],
    queryFn: () => getAssignAreaList(),
    select: (data) => data,
  });
};

export const useCompanyList = (
  params: CompanyListParams,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [...NOTICE_QUERY_KEYS.company, params.region, params.category],
    queryFn: () => getCompanyList(params),
    select: (data) => data,
    enabled: enabled && !!params.region,
  });
};

export const useCompanyMapList = (
  params: CompanyMapListParams,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [
      ...NOTICE_QUERY_KEYS.companyMap,
      params.swLat,
      params.swLng,
      params.neLat,
      params.neLng,
      params.category,
    ],
    queryFn: () => getCompanyMapList(params),
    select: (data) => data,
    enabled:
      enabled &&
      Number.isFinite(params.swLat) &&
      Number.isFinite(params.swLng) &&
      Number.isFinite(params.neLat) &&
      Number.isFinite(params.neLng),
    // 지도 이동마다 자동 재검색되므로 왔던 영역으로 되돌아왔을 때는 캐시를 그대로 쓴다
    staleTime: 60 * 1000,
    // 새 영역을 불러오는 동안 이전 마커를 유지 — 안 그러면 이동할 때마다 마커가 사라졌다 나타난다
    placeholderData: (prev: unknown) => prev,
  });
};

export const useCompanySearch = (
  params: CompanySearchKeywordParams,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [
      ...NOTICE_QUERY_KEYS.companySearch,
      params.keyword,
      params.category,
      params.sort,
      params.latitude,
      params.longitude,
      params.page,
      params.size,
    ],
    queryFn: () => getCompanySearch(params),
    select: (data) => data,
    // 검색어 타이핑 중 이전 결과를 유지해 깜빡임 방지
    placeholderData: (prev) => prev,
    enabled: enabled && !!params.keyword.trim(),
  });
};

export const useCompanyDetail = (
  companySeq: number,
  masterYn?: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [...NOTICE_QUERY_KEYS.companyDetail, companySeq, masterYn],
    queryFn: () => getCompanyDetail(companySeq, masterYn),
    select: (data) => data,
    enabled: enabled && !!companySeq,
  });
};

export const useCompanyTicketList = (
  companySeq: number,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [...NOTICE_QUERY_KEYS.companyTicketList, companySeq],
    queryFn: () => getCompanyTicketList(companySeq),
    select: (data) => data,
    enabled: enabled && !!companySeq,
  });
};

export const useNewNotice = (enabled: boolean = true) => {
  return useQuery({
    queryKey: [...NOTICE_QUERY_KEYS.newNotice],
    queryFn: () => getNewNotice(),
    select: (data) => data,
    enabled: enabled && import.meta.env.VITE_APP_TYPE === "customer",
  });
};

// 보호자 초대 응답 요청 타입
interface GuardianInviteResponseRequest {
  agree: boolean;
}

interface GuardianInviteResponseResponse {
  statusCode: number;
  message: string;
}

// 보호자 초대 응답 API
const respondGuardianInvite = async (
  request: GuardianInviteResponseRequest
): Promise<GuardianInviteResponseResponse> => {
  try {
    const { data } = await privateHttp.post<GuardianInviteResponseResponse>(
      API_ENDPOINTS.MAIN.GUARDIAN_INVITE_RESPONSE,
      request
    );
    return data;
  } catch (error) {
    console.error("보호자 초대 응답 API 호출 에러:", error);
    throw error;
  }
};

// 보호자 초대 응답 React Query Mutation Hook
export const useRespondGuardianInvite = () => {
  return useMutation({
    mutationFn: respondGuardianInvite,
    onError: (error) => {
      console.error("보호자 초대 응답 실패:", error);
    },
  });
};

export { saveUserRegion, getNewNotice };
